/** Channel participants. Account roles are managed in Space settings. */

import { useEffect, useMemo, useState } from 'react'
import type { ReactElement } from 'react'

import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/api/auth'
import { isApiError } from '@/api/client'
import { useAddMembers, useCreateRoom, useMembers, useRemoveMember, useUsers } from '@/api/queries'
import { refreshCachedRoom, usePresence } from '@/api/store'
import type { Member, Room, User } from '@/api/types'
import { UserNameButton } from '@/components/UserCard'
import { Avatar, Button, IconButton, Modal, Spinner, TextField, useToast } from '@/components/ui'
import panel from './DetailsPanel.module.css'
import styles from './MemberList.module.css'

function personName(user: User): string {
  const display = user.display_name.trim()
  return display === '' ? user.username : display
}

function describe(error: unknown, fallback: string): string {
  if (isApiError(error)) return error.message
  if (error instanceof Error && error.message !== '') return error.message
  return fallback
}

/** Keeps a directory search from firing on every keystroke. */
function useDebounced(value: string, delay: number): string {
  const [settled, setSettled] = useState(value)

  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), delay)
    return () => window.clearTimeout(timer)
  }, [value, delay])

  return settled
}

function MessageIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M13.2 10.2a1.4 1.4 0 0 1-1.4 1.4H5.6L3.2 13.6V4.6A1.4 1.4 0 0 1 4.6 3.2h7.2a1.4 1.4 0 0 1 1.4 1.4z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function TrashIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M3.5 4.5h9m-7 0v-1a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v1m-6 0 .6 8a1 1 0 0 0 1 .9h3.8a1 1 0 0 0 1-.9l.6-8"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// ── One member ──────────────────────────────────────────────────────────────

interface MemberRowProps {
  member: Member
  roomId: string
  isSelf: boolean
  online: boolean
  canManage: boolean
  canAssignOwner: boolean
  onRemove: (member: Member) => void
}

function MemberRow({
  member,
  roomId,
  isSelf,
  online,
  canManage,
  canAssignOwner,
  onRemove,
}: MemberRowProps): ReactElement {
  const toast = useToast()
  const navigate = useNavigate()
  const createRoom = useCreateRoom()
  const name = personName(member.user)
  const remote = !member.user.is_local

  // Retain the server's protection for legacy channel owners when removing
  // people. Channel membership no longer offers an account-role selector.
  const removable =
    canManage && !remote && !isSelf && (member.role !== 'owner' || canAssignOwner)

  const openConversation = (): void => {
    if (member.user.is_bot || createRoom.isPending) return
    createRoom.mutate(
      member.user.is_local
        ? { kind: 'direct', member_ids: [member.user.id] }
        : { kind: 'direct', qualified_ids: [member.user.qualified_id] },
      {
        onSuccess: (room) => {
          refreshCachedRoom(room)
          navigate(`/rooms/${room.id}`)
        },
        onError: (error) => toast.error(describe(error, `Could not open a conversation with ${name}.`)),
      },
    )
  }

  return (
    <li className={`${panel.row} ${styles.memberRow}`}>
      <Avatar
        name={name}
        id={member.user.id}
        src={member.user.avatar_url}
        size="sm"
        presence={online ? 'online' : 'offline'}
        decorative
      />

      <div className={panel.rowMain}>
        <span className={panel.rowName}>
          <UserNameButton user={member.user} roomId={roomId} className={styles.memberName}>
            <span className={panel.rowNameText}>{name}</span>
          </UserNameButton>
          {isSelf ? <span className={styles.selfLabel}>(you)</span> : null}
          {member.user.is_bot ? <span className={styles.qualifier}>bot</span> : null}
        </span>
        <span className={panel.rowMeta}>
          {remote ? member.user.qualified_id : `@${member.user.username}`}
          <span className={styles.metaSeparator} aria-hidden="true">·</span>
          Member
        </span>
      </div>

      <div className={panel.rowActions}>
        {isSelf ? (
          <IconButton label="Leave channel" size="sm" variant="ghost" onClick={() => onRemove(member)}>
            <TrashIcon />
          </IconButton>
        ) : removable ? (
          <>
            <IconButton
              label={`Remove ${name} from the room`}
              size="sm"
              variant="ghost"
              onClick={() => onRemove(member)}
            >
              <TrashIcon />
            </IconButton>
          </>
        ) : remote && canManage ? (
          <span className={styles.managedBy}>{`Managed by ${member.user.server_id}`}</span>
        ) : null}
        {member.user.is_bot || isSelf ? null : (
          <IconButton
            className={styles.message}
            label={`Message ${name}`}
            size="sm"
            variant="ghost"
            disabled={createRoom.isPending}
            onClick={openConversation}
          >
            <MessageIcon />
          </IconButton>
        )}
      </div>
    </li>
  )
}

// ── The list ────────────────────────────────────────────────────────────────

export interface MemberListProps {
  room: Room
  canManage: boolean
  canAssignOwner: boolean
  allowAdd?: boolean
}

export function MemberList({ room, canManage, canAssignOwner, allowAdd = true }: MemberListProps): ReactElement {
  const { user } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const presence = usePresence()
  const removeMember = useRemoveMember()
  const addMembers = useAddMembers()
  const { data: members, isLoading, error } = useMembers(room.id)

  const [searchQuery, setSearchQuery] = useState('')
  const search = useDebounced(searchQuery.trim(), 200)
  const { data: users } = useUsers(search)
  const [pendingRemoval, setPendingRemoval] = useState<Member | null>(null)

  const people = useMemo(() => {
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' })
    const all = members ?? []
    return [...all].sort((left, right) => {
      const leftSelf = left.user.id === user?.id ? 0 : 1
      const rightSelf = right.user.id === user?.id ? 0 : 1
      return leftSelf - rightSelf || collator.compare(personName(left.user), personName(right.user))
    })
  }, [members, user?.id])

  const existing = useMemo(
    () => new Set((members ?? []).map((member) => member.user.id)),
    [members],
  )
  const candidates = (users ?? []).filter((candidate) => !existing.has(candidate.id) && candidate.status !== 'disabled').slice(0, 8)

  const remoteServers = useMemo(() => {
    const servers = new Set<string>()
    for (const member of members ?? []) {
      if (!member.user.is_local) servers.add(member.user.server_id)
    }
    return [...servers].sort()
  }, [members])

  const confirmRemoval = (): void => {
    const target = pendingRemoval
    if (target === null) return
    const name = personName(target.user)

    removeMember.mutate(
      { roomId: room.id, memberId: target.id },
      {
        onSuccess: () => {
          const leaving = target.user.id === user?.id
          toast.success(leaving ? 'You left the channel.' : `${name} was removed.`)
          setPendingRemoval(null)
          if (leaving) navigate('/activity', { replace: true })
        },
        onError: (failure) =>
          toast.error(describe(failure, target.user.id === user?.id ? 'Could not leave the channel.' : `Could not remove ${name}.`)),
      },
    )
  }

  if (error !== null) {
    return (
      <p className={panel.error} role="alert">
        {describe(error, 'The member list could not be loaded.')}
      </p>
    )
  }

  if (isLoading || members === undefined) {
    return (
      <div className={panel.loading}>
        <Spinner label="Loading members" />
      </div>
    )
  }

  return (
    <div className={panel.stack}>
      {canManage && allowAdd ? (
        <div className={styles.inlineSearch}>
          <TextField label="Add people" type="search" placeholder="Search people to add" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} />
          {search !== '' && candidates.length > 0 ? <ul className={styles.results}>{candidates.map((candidate) => <li key={candidate.id}><button type="button" className={styles.result} onClick={() => addMembers.mutate({ roomId: room.id, body: { member_ids: [candidate.id] } }, { onSuccess: () => setSearchQuery('') })}><Avatar name={personName(candidate)} id={candidate.id} src={candidate.avatar_url} size="sm" decorative /><span>{personName(candidate)} <small>@{candidate.username}</small></span></button></li>)}</ul> : null}
        </div>
      ) : null}
      {people.length === 0 ? (
        <p className={panel.empty}>Nobody has joined yet.</p>
      ) : (
        <ul className={panel.list}>
          {people.map((member) => (
            <MemberRow key={member.id} member={member} roomId={room.id} isSelf={member.user.id === user?.id}
              online={presence.get(member.user.id)?.status === 'online'} canManage={canManage}
              canAssignOwner={canAssignOwner} onRemove={setPendingRemoval} />
          ))}
        </ul>
      )}

      {remoteServers.length > 0 ? (
        <p className={panel.note}>
          {`People from ${remoteServers.join(', ')} take part through channel sharing. Their accounts are managed on that server.`}
        </p>
      ) : null}

      {!room.is_local ? (
        <p className={panel.note}>
          {`This room is owned by ${room.owner_server_id}. Membership is decided there.`}
        </p>
      ) : null}

      <Modal
        open={pendingRemoval !== null}
        onClose={() => setPendingRemoval(null)}
        title={pendingRemoval?.user.id === user?.id ? 'Leave channel' : 'Remove from room'}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingRemoval(null)}>
              Cancel
            </Button>
            <Button variant="danger" loading={removeMember.isPending} onClick={confirmRemoval}>
              {pendingRemoval?.user.id === user?.id ? 'Leave' : 'Remove'}
            </Button>
          </>
        }
      >
        <p className={panel.sectionHint}>
          {pendingRemoval === null
            ? ''
            : pendingRemoval.user.id === user?.id
              ? 'You will lose access to this room and everything posted in it. You can be added again later.'
              : `${personName(pendingRemoval.user)} will lose access to this room and everything posted in it. They can be added again later.`}
        </p>
      </Modal>
    </div>
  )
}

export default MemberList
